import { memo, useCallback, useEffect } from 'react';
import { Address, Conversation, mapClient, useAddress } from 'src/graphql';
import { AnySearchableEntity } from 'src/graphql/entities/search/use-search';
import { EntityNames } from 'src/graphql/types';
import { Modals, useModalMethods } from 'src/store/modals';
import { useConfirmationModal } from 'src/utils/hooks';

const GenericModalSetComponent = ({
  entityName,
  selectedRecord,
}: {
  entityName: EntityNames;
  selectedRecord: AnySearchableEntity | null;
}) => {
  const { confirm } = useConfirmationModal();
  const { setModalProps: setAddSiteModalProps } = useModalMethods(Modals.AddSiteModal);
  const { setModalProps: setAddClientModalProps } = useModalMethods(Modals.AddClientModal);
  const { setModalProps: setMergeConversationModalProps } = useModalMethods(
    Modals.MergeConversationModal,
  );
  const { setModalProps: setSnoozeConversationModalProps } = useModalMethods(
    Modals.SnoozeConversationModal,
  );
  const { setModalProps: setConversationToAddressModalProps } = useModalMethods(
    Modals.UpdateConversationToAddress,
  );

  const { createConversationToAddress, deleteConversationToAddress } = useAddress();

  const onAddNewSite = useCallback(
    async (newSite: Address, conversation: Conversation) => {
      if (
        conversation &&
        conversation.conversationId &&
        (await confirm({
          title: '',
          body: 'Do you want to associate this site \nwith the current conversation?',
          variant: 'yesNo',
        }))
      ) {
        await deleteConversationToAddress(
          conversation.conversationId,
          conversation?.requester?.partnerId,
        );
        await createConversationToAddress(
          conversation.conversationId,
          newSite,
          newSite.partnerId,
        );
      }
    },
    [confirm, createConversationToAddress, deleteConversationToAddress],
  );

  useEffect(() => {
    let deleteAddSiteModalProps: () => void;
    let deleteAddClientModalProps: () => void;
    let deleteMergeConversationModalProps: () => void;
    let deleteSnoozeConversationModalProps: () => void;
    let deleteConversationToAddressModalProps: () => void;
    if (entityName === EntityNames.Conversation && selectedRecord) {
      const conversation = selectedRecord as Conversation;

      const client =
        conversation?.requester?.participantEntity === 'client' &&
        conversation?.requester.client
          ? mapClient(conversation.requester.client)
          : null;

      deleteAddSiteModalProps = setAddSiteModalProps({
        partnerId: conversation?.partnerId,
        ...(client
          ? {
              client: {
                partnerId: client.partnerId,
                id: client.id,
                name: client.fullName,
                email: client.primaryEmail,
              },
            }
          : {}),
        onSuccessSubmit: (newSite: Address) => onAddNewSite(newSite, conversation),
      });
      deleteAddClientModalProps = setAddClientModalProps({
        conversation,
      });
      deleteMergeConversationModalProps = setMergeConversationModalProps({
        conversation,
      });
      deleteSnoozeConversationModalProps = setSnoozeConversationModalProps({
        conversationStatusId: conversation?.conversationStatusId,
        snoozeUntil: conversation?.snoozeUntil,
        partnerId: conversation?.partnerId,
        friendlyId: conversation?.friendlyId,
        subject: conversation?.subject,
        conversationId: conversation?.conversationId,
      });
      deleteConversationToAddressModalProps = setConversationToAddressModalProps({
        conversation,
      });
    }
    return () => {
      if (entityName === EntityNames.Conversation) {
        deleteAddSiteModalProps?.();
        deleteAddClientModalProps?.();
        deleteMergeConversationModalProps?.();
        deleteSnoozeConversationModalProps?.();
        deleteConversationToAddressModalProps?.();
      }
    };
  }, [
    selectedRecord,
    setAddSiteModalProps,
    setAddClientModalProps,
    entityName,
    setMergeConversationModalProps,
    setSnoozeConversationModalProps,
    setConversationToAddressModalProps,
    onAddNewSite,
  ]);

  return null;
};

export const GenericModalSet = memo(GenericModalSetComponent);
